import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

const ThemeContext = createContext();
export const THEME_PREFERENCES = ['system', 'light', 'dark'];

const getSystemTheme = () => (
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
);

const isThemePreference = (value) => THEME_PREFERENCES.includes(value);

const getInitialPreference = () => {
  const savedPreference = localStorage.getItem('theme_preference');
  if (isThemePreference(savedPreference)) {
    return savedPreference;
  }

  const legacyTheme = localStorage.getItem('theme');
  return legacyTheme === 'light' || legacyTheme === 'dark' ? legacyTheme : 'system';
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(getInitialPreference);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const theme = preference === 'system' ? systemTheme : preference;

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => setSystemTheme(event.matches ? 'dark' : 'light');

    setSystemTheme(media.matches ? 'dark' : 'light');
    media.addEventListener('change', handleChange);

    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme_preference', preference);
  }, [preference, theme]);

  const setThemePreference = useCallback((nextPreference) => {
    if (isThemePreference(nextPreference)) {
      setPreference(nextPreference);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme]);

  const value = useMemo(() => ({
    theme,
    preference,
    setThemePreference,
    toggleTheme,
    isDark: theme === 'dark'
  }), [theme, preference, setThemePreference, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
